import React, { FC, useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperCore } from 'swiper';
import { FreeMode, Mousewheel, Navigation, Thumbs } from 'swiper/modules';
import LightGallery from 'lightgallery/react';
import 'lightgallery/css/lightgallery-bundle.css';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgVideo from 'lightgallery/plugins/video';
import 'lightgallery/css/lightgallery.css';
import 'lightgallery/css/lg-zoom.css';
import 'lightgallery/css/lg-thumbnail.css';
import 'lightgallery/css/lg-video.css';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import s from './SwiperComponent.module.scss';
import { BACKEND_URL } from '../../api';

import { ReactComponent as PlayButton } from '../../assets/icons/play-button.svg';

interface Iimages {
    id: number;
    type: string;
    src: string;
    poster?: string;
}

interface DataProps {
    data: Iimages[];
}

const SwiperComponent: FC<DataProps> = ({ data }) => {
    const [thumbsSwiper, setThumbsSwiper] = useState<SwiperCore | null>(null);
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const pausedVideosRef = useRef<Set<HTMLVideoElement>>(new Set());

    const customStyle: React.CSSProperties & { [key: string]: string } = {
        '--swiper-navigation-color': '#fff',
        '--swiper-pagination-color': '#fff',
    };

    useEffect(() => {
        const videoElements = document.querySelectorAll(`.${s.generalSlide} video`) as NodeListOf<HTMLVideoElement>;
        
        // Проверяем, что lightgallery не открыт
        const isLightGalleryOpen = document.querySelector('.lg-on') !== null;
        if (isLightGalleryOpen) {
            // Если lightgallery открыт, останавливаем все видео
            videoElements.forEach((video) => {
                if (video && document.body.contains(video)) {
                    try {
                        video.pause();
                        video.currentTime = 0;
                    } catch (err) {
                        // Игнорируем ошибки при остановке видео
                    }
                }
            });
            return;
        }
        
        // Обработчики событий для отслеживания паузы пользователем
        const pauseHandlers: Map<HTMLVideoElement, () => void> = new Map();
        
        videoElements.forEach((video) => {
            // Проверяем, что элемент все еще в DOM
            if (!document.body.contains(video)) {
                return;
            }
            
            const handlePause = () => {
                // Если видео на активном слайде и было поставлено на паузу, добавляем в список
                if (video.closest('.swiper-slide-active') && document.body.contains(video)) {
                    pausedVideosRef.current.add(video);
                }
            };
            
            video.addEventListener('pause', handlePause);
            pauseHandlers.set(video, handlePause);
        });
        
        // Обработка активного слайда
        videoElements.forEach((video) => {
            // Проверяем, что элемент все еще в DOM
            if (!document.body.contains(video)) {
                return;
            }
            
            const isActive = video.closest('.swiper-slide-active');
            
            if (isActive) {
                // Если видео было принудительно поставлено на паузу пользователем, не запускаем его автоматически
                if (!pausedVideosRef.current.has(video)) {
                    video.muted = true; // Видео всегда без звука
                    const playPromise = video.play();
                    
                    // Правильно обрабатываем Promise от play()
                    if (playPromise !== undefined) {
                        playPromise
                            .then(() => {
                                // Воспроизведение началось успешно
                                // Проверяем, что элемент все еще в DOM
                                if (!document.body.contains(video)) {
                                    try {
                                        video.pause();
                                    } catch (e) {
                                        // Игнорируем ошибки
                                    }
                                }
                            })
                            .catch((err) => {
                                // Игнорируем ошибки, связанные с прерыванием воспроизведения
                                // или если элемент был удален из DOM
                                if (err.name !== 'NotAllowedError' && 
                                    err.name !== 'AbortError' &&
                                    document.body.contains(video)) {
                                    // Логируем только реальные ошибки
                                    console.error('Error playing video:', err);
                                }
                            });
                    }
                }
            } else {
                try {
                    // Проверяем наличие элемента перед паузой
                    if (document.body.contains(video)) {
                        video.pause();
                        video.currentTime = 0;
                    }
                } catch (err) {
                    // Игнорируем ошибки при остановке видео
                }
                // Убираем из списка приостановленных при смене слайда
                pausedVideosRef.current.delete(video);
            }
        });
        
        // Очистка обработчиков
        return () => {
            pauseHandlers.forEach((handler, video) => {
                if (document.body.contains(video)) {
                    video.removeEventListener('pause', handler);
                }
            });
        };
    }, [activeIndex]);

    const handleGalleryOpen = () => {
        document.body.style.overflow = 'hidden';
        // Останавливаем все видео в основном слайдере при открытии lightgallery
        const videoElements = document.querySelectorAll(`.${s.generalSlide} video`) as NodeListOf<HTMLVideoElement>;
        videoElements.forEach((video) => {
            if (video && document.body.contains(video)) {
                try {
                    video.pause();
                    video.currentTime = 0;
                } catch (err) {
                    // Игнорируем ошибки
                }
            }
        });
    };

    const handleGalleryClose = () => {
        document.body.style.overflow = 'auto';
        // Очищаем список приостановленных видео при закрытии lightgallery
        pausedVideosRef.current.clear();
    };



    // Создаем массив всех элементов
    const allItems = data;

    return (
        <div className={s.wrapper}>
            <LightGallery
                selector='.gallery-item'
                elementClassNames={s.custom_wrapper_class}
                speed={0}
                plugins={[lgThumbnail, lgVideo]}
                download={false}
                counter={false}
                onAfterOpen={handleGalleryOpen}
                onAfterClose={handleGalleryClose}
            >
                <Swiper
                    style={customStyle}
                    loop={true}
                    spaceBetween={10}
                    navigation={true}
                    thumbs={{ swiper: thumbsSwiper }}
                    modules={[FreeMode, Navigation, Thumbs]}
                    className={s.generalSwiper}
                    onSlideChange={(swiper: SwiperCore) => setActiveIndex(swiper.realIndex)}
                >
                    {allItems.map((item, index) => {
                        const videoUrl = BACKEND_URL + item.src;
                        const posterUrl = item.poster ? BACKEND_URL + item.poster : undefined;
                        
                        // Создаем JSON строку вручную, как в документации
                        // Добавляем постер, если он есть
                        const videoDataObj: {
                            source: Array<{ src: string; type: string }>;
                            attributes: {
                                preload: boolean;
                                playsinline: boolean;
                                controls: boolean;
                                poster?: string;
                            };
                        } = {
                            source: [{ src: videoUrl, type: "video/mp4" }],
                            attributes: {
                                preload: false,
                                playsinline: true,
                                controls: true
                            }
                        };
                        
                        // Добавляем постер в атрибуты, если он есть
                        if (posterUrl) {
                            videoDataObj.attributes.poster = posterUrl;
                        }
                        
                        const videoData = JSON.stringify(videoDataObj);

                        return (
                            <SwiperSlide className={`${s.generalSlide} lg-item`} key={index}>
                            {item.type === 'image' ? (
                              <a
                                className='gallery-item'
                                href={videoUrl} // для изображений LightGallery использует href
                                data-src={videoUrl}
                              >
                                <img src={videoUrl} alt={item.id.toString()} />
                              </a>
                            ) : (
                              <a
                                className='gallery-item'
                                data-video={videoData}     // JSON с видео для LightGallery
                                data-poster={posterUrl}    // LightGallery использует постер
                                data-lg-size="1280-720"
                              >
                                {/* В Swiper отображаем и видео с автоплеем */}
                                <video
                                  muted
                                  controls
                                  poster={posterUrl}       // постер для Swiper
                                  autoPlay
                                >
                                  <source src={videoUrl} type="video/mp4" />
                                  Your browser does not support the video tag.
                                </video>
                          
                                {/* LightGallery постер */}
                                {posterUrl && <img src={posterUrl} alt={item.id.toString()} style={{ display: 'none' }} />}
                              </a>
                            )}
                          </SwiperSlide>
                          
                        );
                    })}
                </Swiper>
            </LightGallery>
            <Swiper
                onSwiper={setThumbsSwiper}
                direction="horizontal"
                spaceBetween={15}
                watchSlidesProgress={true}
                modules={[FreeMode, Navigation, Thumbs, Mousewheel]}
                className={s.navigationSwiper}
                grabCursor
                breakpoints={{
                    300: {
                        direction: 'horizontal',
                        slidesPerView: 3.4,
                        slidesPerGroup: 3.4,
                    },
                    992: {
                        direction: 'horizontal',
                        slidesPerView: 4.5,
                        slidesPerGroup: 4.5,
                    },
                }}
                mousewheel
            >
                {allItems.map((item, index) => {
                    const posterUrl = item.poster ? BACKEND_URL + item.poster : undefined;
                    
                    return (
                        <SwiperSlide className={s.slide} key={index}>
                            {item.type === 'image' ? (
                                <img src={BACKEND_URL + item.src} alt={item.id.toString()} />
                            ) : (
                                <>
                                    {posterUrl ? (
                                        <img src={posterUrl} alt={item.id.toString()} />
                                    ) : (
                                        <video controls={false} muted>
                                            <source src={BACKEND_URL + item.src} type="video/mp4" />
                                            Your browser does not support the video tag.
                                        </video>
                                    )}
                                    <PlayButton className={s.play_button} />
                                </>
                            )}
                        </SwiperSlide>
                    );
                })}
            </Swiper>
        </div>
    );
};

export default SwiperComponent;

